import React from 'react'
import { Tooltip } from 'antd'
import styled from 'styled-components/macro'
import {
  SUCCESS_HUB_PROGRESS_TOKENS,
  successHubProgressSurfaceBorder,
} from 'src/utils/successHubProgressTokens'
import { H1 } from 'src/branding/typography'
import { FOOD_SAFETY_SURFACE } from 'src/constants/foodSafety'

const T = SUCCESS_HUB_PROGRESS_TOKENS

/**
 * Progress header card — short why + slim ordering streak + quiet streak settings.
 */
const SuccessHubProgressValueHero = ({
  summary = null,
  daysEditor = null,
  className,
  'data-testid': dataTestId = 'progress-value-hero',
}) => {
  const streak = summary?.streaks?.[0] || null
  const hint = streak
    ? summary?.hint || 'Change days if this kitchen’s rhythm shifts.'
    : null

  return (
    <Card
      className={className}
      data-testid={dataTestId}
      data-days-confirmed={
        summary ? (summary.daysConfirmed ? 'true' : 'false') : undefined
      }
    >
      <Title>Success Hub</Title>
      <Why data-testid={`${dataTestId}-why`}>
        Get the most out of OMS — see what’s already set up for this kitchen,
        and what useful features you haven’t tried yet.
      </Why>
      {streak ? (
        <Tooltip
          title={streak.tooltip}
          mouseEnterDelay={0.25}
          placement='bottom'
        >
          <Streak
            data-testid='progress-summary'
            data-days-confirmed={summary.daysConfirmed ? 'true' : 'false'}
            tabIndex={0}
          >
            <StreakMain
              data-testid={`progress-summary-streak-${streak.streakId}`}
              data-current={String(streak.current)}
              data-longest={String(streak.longest)}
              data-enabled={streak.enabled ? 'true' : 'false'}
            >
              <Line>
                {/* Name the streak once — no hidden copy or aria-label. */}
                <Label data-testid='progress-summary-label'>
                  <span
                    data-testid={`progress-summary-streak-${streak.streakId}-name`}
                  >
                    {streak.label || 'Ordering streak'}
                  </span>
                </Label>
                <Dot aria-hidden='true'>·</Dot>
                <Count
                  data-testid={`progress-summary-streak-${streak.streakId}-count`}
                >
                  {streak.currentLabel}
                </Count>
                <Unit
                  data-testid={`progress-summary-streak-${streak.streakId}-unit`}
                >
                  {streak.unitLabel}
                </Unit>
                {streak.bestLabel ? (
                  <Best
                    data-testid={`progress-summary-streak-${streak.streakId}-best`}
                  >
                    {streak.bestLabel}
                  </Best>
                ) : null}
              </Line>
              {hint ? (
                <Hint data-testid='progress-summary-hint'>{hint}</Hint>
              ) : null}
            </StreakMain>
          </Streak>
        </Tooltip>
      ) : null}
      {daysEditor ? (
        <DaysSlot data-testid={`${dataTestId}-days`}>{daysEditor}</DaysSlot>
      ) : null}
    </Card>
  )
}

export default SuccessHubProgressValueHero

const Card = styled.header`
  background: ${FOOD_SAFETY_SURFACE.HERO_BG};
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  border-top: 3px solid
    ${({ theme }) => theme?.colours?.lettuceGreen || '#b5d922'};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  display: flex;
  flex-direction: column;
  gap: ${T.space.sm};
  padding: ${T.pad.surface};
  width: 100%;
`

const Title = styled(H1)`
  margin: 0;
  font-size: 1.75rem;
  line-height: 1.15;
  color: ${({ theme }) => theme?.colours?.olive || '#132d07'};
`

const Why = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
  font-size: ${T.type.body};
  font-weight: 500;
  line-height: 1.45;
  margin: 0;
`

const Streak = styled.div`
  border-top: ${({ theme }) => successHubProgressSurfaceBorder(theme)};
  cursor: help;
  padding-top: ${T.space.sm};
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
    outline-offset: 2px;
  }
`

const StreakMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const Line = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  width: 100%;
`

const Label = styled.p`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: ${T.type.body};
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
`

const Dot = styled.span`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
`

const Count = styled.p`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: ${T.type.display};
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
  margin: 0;
`

const Unit = styled.span`
  border: 0;
  clip: rect(0, 0, 0, 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

const Best = styled.span`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  font-size: ${T.type.lead};
  font-weight: 600;
  line-height: 1.3;
  margin-left: 4px;
`

const Hint = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  font-size: ${T.type.lead};
  font-weight: 500;
  line-height: 1.35;
  margin: 0;
  max-width: 40rem;
`

const DaysSlot = styled.div`
  border-top: ${({ theme }) => successHubProgressSurfaceBorder(theme)};
  padding-top: ${T.space.sm};
  width: 100%;
`
